import { useEffect, useState } from 'preact/hooks';
import { createPortal } from 'preact/compat';
import { api, ApiError } from '../api/client';
import { useAsync } from '../hooks/useAsync';
import type { ListLicenseModelsResponse } from '../api/types';
import { GeoPickerModal } from './GeoPickerModal';
import { IconPickOnMap, IconCancel, IconCreate } from '../icons/HvacIcons';
import './hv-modal.css';

interface Props {
  /** Scopes, in denen angelegt werden darf. Seit 13.10.2026 genau einer: der
   *  aktive Scope der Sitzung (der Server lehnt jeden anderen ab). */
  scopes: Array<{ id: string; name: string }>;
  onCreated(payload: { id: string; displayName: string; dataScopeId: string }): void;
  onClose(): void;
}

/** POST /api/plants form. Required: DataScopeId, DisplayName, LicenseModelId.
 *  Optional metadata (address, coords) can be filled here or later via edit.
 *
 *  Das Lizenzmodell ist Pflicht (Telemetrie-Konzept §4.1): es legt die
 *  Reglergrenze der Anlage fest, und ohne Grenzen gibt es keine Anlage. Die
 *  Liste zeigt nur zuweisbare Modelle — deaktivierte tauchen bewusst nicht
 *  auf. Vorbelegt wird das erste, damit der Normalfall ein Klick bleibt; die
 *  Grenzen stehen am Feld, damit die Wahl nicht blind passiert. */
export function CreatePlantModal({ scopes, onCreated, onClose }: Props) {
  const [dataScopeId, setDataScopeId] = useState(scopes[0]?.id ?? '');
  const [licenseModelId, setLicenseModelId] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [address, setAddress] = useState('');
  const [latitude, setLatitude] = useState('');
  const [longitude, setLongitude] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [geoPickerOpen, setGeoPickerOpen] = useState(false);

  // Zuweisbare Lizenzmodelle. Kein includeInactive: ein deaktiviertes Modell
  // wuerde der Server bei POST /api/plants ohnehin ablehnen.
  const modelsState = useAsync(
    (signal) => api.get<ListLicenseModelsResponse>('/api/licensing/models', signal),
    [],
  );
  const licenseModels = modelsState.status === 'ok' ? modelsState.data.items : [];

  // Vorbelegung, sobald die Liste da ist. Nur wenn noch nichts gewaehlt ist —
  // sonst wuerde ein Refresh die Auswahl des Operators zuruecksetzen.
  useEffect(() => {
    if (licenseModelId === '' && licenseModels.length > 0) {
      setLicenseModelId(licenseModels[0].id);
    }
  }, [licenseModelId, licenseModels]);

  useEffect(() => {
    const h = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [onClose]);

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      const dn = displayName.trim();
      if (dn.length === 0) throw new Error('Anzeigename darf nicht leer sein.');
      if (!dataScopeId) throw new Error('DataScope muss ausgewählt sein.');
      if (!licenseModelId) throw new Error('Lizenzmodell muss ausgewählt sein.');
      const body: Record<string, unknown> = { dataScopeId, displayName: dn, licenseModelId };
      if (address.trim() !== '') body.address = address.trim();
      if (latitude.trim() !== '') {
        const n = Number(latitude);
        if (!Number.isFinite(n)) throw new Error('Latitude ist keine gültige Zahl.');
        body.latitude = n;
      }
      if (longitude.trim() !== '') {
        const n = Number(longitude);
        if (!Number.isFinite(n)) throw new Error('Longitude ist keine gültige Zahl.');
        body.longitude = n;
      }
      const res = await api.post<{ id: string; displayName: string; dataScopeId: string }>(`/api/plants`, body);
      onCreated(res);
    } catch (e) {
      if (e instanceof ApiError) {
        const bodyMsg = (e.body as { error?: string })?.error;
        setError(bodyMsg ?? `Anlegen fehlgeschlagen (${e.status}).`);
      } else if (e instanceof Error) {
        setError(e.message);
      } else {
        setError('Unbekannter Fehler beim Anlegen.');
      }
      setSaving(false);
    }
  };

  return createPortal(
    <div class="hv-modal-backdrop" onClick={onClose}>
      <div class="hv-modal" onClick={(e) => e.stopPropagation()}>
        <div class="hv-modal__header">
          <h2 class="hv-modal__title">Neue Anlage</h2>
          <button type="button" class="hv-modal__close" onClick={onClose} aria-label="Schließen">×</button>
        </div>
        <div class="hv-modal__body">
          <div class="hv-modal__field">
            <label>DataScope</label>
            <select value={dataScopeId} onChange={(e) => setDataScopeId((e.target as HTMLSelectElement).value)}>
              {scopes.map((s) => (
                <option key={s.id} value={s.id}>{s.name}</option>
              ))}
            </select>
            <span class="hv-modal__hint">Neue Anlagen entstehen im aktiven DataScope der Sitzung.</span>
          </div>
          <div class="hv-modal__field">
            <label>Anzeigename</label>
            <input type="text" value={displayName} onInput={(e) => setDisplayName((e.target as HTMLInputElement).value)} placeholder="z.B. Wohnanlage Musterstraße" />
          </div>
          <div class="hv-modal__field">
            <label>Lizenzmodell</label>
            <select value={licenseModelId}
                    onChange={(e) => setLicenseModelId((e.target as HTMLSelectElement).value)}>
              {licenseModels.length === 0 && <option value="">Keine zuweisbaren Modelle</option>}
              {licenseModels.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.name} — {m.maxControllersPerPlant} Regler / {m.maxRecordedDatapointsPerController} DPs je Regler
                </option>
              ))}
            </select>
            {modelsState.status === 'error' && (
              <span class="hv-modal__hint">Lizenzmodelle konnten nicht geladen werden.</span>
            )}
            {modelsState.status === 'ok' && licenseModels.length === 0 && (
              <span class="hv-modal__hint">
                Es gibt kein zuweisbares Lizenzmodell. Ohne Modell kann keine Anlage angelegt
                werden — unter Einstellungen → Lizenzmodelle eines anlegen oder aktivieren.
              </span>
            )}
          </div>
          <div class="hv-modal__field">
            <label>Anschrift</label>
            <input type="text" value={address} onInput={(e) => setAddress((e.target as HTMLInputElement).value)} placeholder="optional" />
          </div>
          <div class="hv-modal__row">
            <div class="hv-modal__field">
              <label>Latitude</label>
              <input type="text" value={latitude} onInput={(e) => setLatitude((e.target as HTMLInputElement).value)} placeholder="optional" />
            </div>
            <div class="hv-modal__field">
              <label>Longitude</label>
              <input type="text" value={longitude} onInput={(e) => setLongitude((e.target as HTMLInputElement).value)} placeholder="optional" />
            </div>
          </div>
          <div>
            <button type="button" class="hv-btn hv-btn--sm"
                    onClick={() => setGeoPickerOpen(true)}
                    title="Position auf einer Karte auswählen">
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                <IconPickOnMap size={14} />
                Auf Karte wählen…
              </span>
            </button>
          </div>
          {error && <p class="hv-modal__error">{error}</p>}
        </div>
        <div class="hv-modal__footer">
          <button type="button" class="hv-btn" onClick={onClose} disabled={saving}>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><IconCancel size={14} />Abbrechen</span>
          </button>
          <button type="button" class="hv-btn hv-btn--primary" onClick={save} disabled={saving || scopes.length === 0 || licenseModelId === ''}>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><IconCreate size={14} />Anlegen</span>
          </button>
        </div>
      </div>
      {geoPickerOpen && (
        <GeoPickerModal
          initialLat={parseCoord(latitude)}
          initialLon={parseCoord(longitude)}
          initialAddress={address}
          onPick={(lat, lon, _elevationM) => {
            setLatitude(lat.toFixed(6));
            setLongitude(lon.toFixed(6));
          }}
          onClose={() => setGeoPickerOpen(false)}
        />
      )}
    </div>,
    document.body,
  );
}

function parseCoord(raw: string): number | null {
  const n = Number(raw.trim());
  return Number.isFinite(n) && raw.trim() !== '' ? n : null;
}
